import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CalendarIcon, AlertTriangle } from "lucide-react";
import { format, addDays, addMonths, addYears } from "date-fns";
import { ko } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Plan, SubscriptionStatus, Platform } from "@/types/enums";
import type { AdminSubscriptionItem } from "@/types/subscription";
import {
  useExtendSubscription,
  useExtendBilledSubscription,
} from "@/hooks/use-subscriptions";
import { PlanBadge } from "./plan-badge";

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.ACTIVE]: "활성",
  [SubscriptionStatus.PENDING_PAYMENT]: "결제 대기",
  [SubscriptionStatus.PENDING_CANCELLATION]: "해지 대기",
  [SubscriptionStatus.PENDING_PLAN_CHANGE]: "플랜 변경 대기",
};

type ExtendMode = "duration_days" | "end_date" | "months";

const BILLED_PLANS: ReadonlySet<Plan> = new Set([
  Plan.PRO,
  Plan.PLUS,
  Plan.PREMIUM,
]);

function getExtendMode(plan: Plan): ExtendMode | null {
  if (plan === Plan.PRO_TRIAL) return "duration_days";
  if (plan === Plan.STUDENT_PASS) return "end_date";
  if (BILLED_PLANS.has(plan)) return "months";
  return null;
}

function getExtendSchema(mode: ExtendMode | null, maxMonths: number) {
  return z
    .object({
      durationDays: z.number().int().min(1).max(365).optional(),
      endDate: z.date().optional(),
      months: z.string().optional(),
      reason: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (mode === "duration_days" && (!data.durationDays || data.durationDays <= 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "기간(일)을 입력해야 합니다",
          path: ["durationDays"],
        });
      }
      if (mode === "end_date" && !data.endDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "종료일을 선택해야 합니다",
          path: ["endDate"],
        });
      }
      if (mode === "months") {
        const trimmed = (data.months ?? "").trim();
        if (!trimmed) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "값을 입력하세요",
            path: ["months"],
          });
        } else if (!/^\d+$/.test(trimmed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "정수만 입력 가능합니다",
            path: ["months"],
          });
        } else {
          const parsed = Number(trimmed);
          if (parsed < 1 || parsed > maxMonths) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `1 ~ ${maxMonths} 사이의 값이어야 합니다`,
              path: ["months"],
            });
          }
        }
      }
    });
}

// iOS 는 Apple extendByDays 한도(90일) 때문에 한 번에 최대 3개월까지만 연장 가능.
function getMaxExtendMonths(platform: AdminSubscriptionItem["platform"]): number {
  return platform === Platform.IOS ? 3 : 24;
}

type ExtendFormValues = z.infer<ReturnType<typeof getExtendSchema>>;

function canExtend(subscription: AdminSubscriptionItem): {
  allowed: boolean;
  reason?: string;
} {
  const mode = getExtendMode(subscription.plan);
  if (!mode) {
    return {
      allowed: false,
      reason: "이 플랜은 연장 대상이 아닙니다.",
    };
  }

  if (mode === "months") {
    // TOSS / STRIPE / IOS / ANDROID 모두 외부 API 호출로 연장 처리.
  } else {
    if (subscription.platform === Platform.IOS) {
      return {
        allowed: false,
        reason: "IOS 사용자는 App Store에서 구독을 관리해야 합니다.",
      };
    }
    if (subscription.platform === Platform.ANDROID) {
      return {
        allowed: false,
        reason: "Android 사용자는 Play Store에서 구독을 관리해야 합니다.",
      };
    }
    if (subscription.platform === Platform.STRIPE) {
      return {
        allowed: false,
        reason: "STRIPE 사용자는 Stripe 대시보드에서 구독을 관리해야 합니다.",
      };
    }
  }

  if (subscription.status === SubscriptionStatus.PENDING_PAYMENT) {
    return {
      allowed: false,
      reason: `현재 구독 상태가 "${STATUS_LABEL[subscription.status]}"입니다. 활성 상태의 구독만 연장할 수 있습니다.`,
    };
  }
  if (subscription.status === SubscriptionStatus.PENDING_PLAN_CHANGE) {
    return {
      allowed: false,
      reason: `현재 구독 상태가 "${STATUS_LABEL[subscription.status]}"입니다. 기존 변경을 취소한 후 진행해주세요.`,
    };
  }

  if (!subscription.nextPaymentDate) {
    return {
      allowed: false,
      reason: "다음 결제일 정보가 없어 연장할 수 없습니다.",
    };
  }

  return { allowed: true };
}

interface ExtendDialogProps {
  subscription: AdminSubscriptionItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExtendDialog({
  subscription,
  open,
  onOpenChange,
}: ExtendDialogProps) {
  const { mutate: extend, isPending: isExtendPending } = useExtendSubscription();
  const { mutate: extendBilled, isPending: isBilledPending } =
    useExtendBilledSubscription();
  const extendCheck = canExtend(subscription);
  const mode = getExtendMode(subscription.plan);
  const isProTrial = mode === "duration_days";
  const isStudentPass = mode === "end_date";
  const isBilled = mode === "months";
  const isPending = isExtendPending || isBilledPending;
  const maxMonths = getMaxExtendMonths(subscription.platform);

  const currentEndDate = subscription.nextPaymentDate
    ? new Date(subscription.nextPaymentDate)
    : null;

  const form = useForm<ExtendFormValues>({
    resolver: zodResolver(getExtendSchema(mode, maxMonths)),
    defaultValues: {
      durationDays: isProTrial ? 30 : undefined,
      endDate: undefined,
      months: "",
      reason: "",
    },
  });

  const watchDurationDays = form.watch("durationDays");
  const watchEndDate = form.watch("endDate");
  const watchMonths = form.watch("months");

  const parsedMonths = useMemo(() => {
    const trimmed = (watchMonths ?? "").trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const parsed = Number(trimmed);
    if (parsed < 1 || parsed > maxMonths) return null;
    return parsed;
  }, [watchMonths, maxMonths]);

  const extendedEndDate = useMemo(() => {
    if (isProTrial && watchDurationDays && currentEndDate) {
      return addDays(currentEndDate, watchDurationDays);
    }
    if (isStudentPass && watchEndDate) {
      return watchEndDate;
    }
    if (isBilled && parsedMonths && currentEndDate) {
      return addMonths(currentEndDate, parsedMonths);
    }
    return null;
  }, [
    isProTrial,
    isStudentPass,
    isBilled,
    watchDurationDays,
    watchEndDate,
    parsedMonths,
    currentEndDate,
  ]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) form.reset();
    onOpenChange(isOpen);
  };

  const onSubmit = (values: ExtendFormValues) => {
    if (isBilled) {
      if (!values.months) return;
      extendBilled(
        {
          userId: subscription.userId,
          months: Number(values.months),
          reason: values.reason,
        },
        {
          onSuccess: () => {
            handleOpenChange(false);
          },
        }
      );
      return;
    }

    extend(
      {
        userId: subscription.userId,
        plan: subscription.plan,
        durationDays: isProTrial ? values.durationDays : undefined,
        endDate: isStudentPass ? values.endDate?.toISOString() : undefined,
        reason: values.reason,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>구독 연장</DialogTitle>
          <DialogDescription>
            현재 플랜의 기간을 연장합니다.
          </DialogDescription>
        </DialogHeader>

        {!extendCheck.allowed ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>연장 불가</AlertTitle>
            <AlertDescription>{extendCheck.reason}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4 py-4">
            <div className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">사용자</span>
                <span className="text-sm">{subscription.userName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">현재 플랜</span>
                <PlanBadge plan={subscription.plan} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">현재 종료일</span>
                <span className="text-sm">
                  {currentEndDate
                    ? format(currentEndDate, "PPP", { locale: ko })
                    : "-"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">예상 종료일</span>
                <span
                  className={cn(
                    "text-sm font-semibold",
                    extendedEndDate
                      ? "text-emerald-600"
                      : "text-muted-foreground font-normal"
                  )}
                >
                  {extendedEndDate
                    ? format(extendedEndDate, "PPP", { locale: ko })
                    : "-"}
                </span>
              </div>
            </div>

            {subscription.status ===
              SubscriptionStatus.PENDING_CANCELLATION && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  해지 대기 중인 구독입니다. 연장하면 해지가 취소됩니다.
                </AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                {isBilled && (
                  <FormField
                    control={form.control}
                    name="months"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>연장 개월 수 *</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            inputMode="numeric"
                            placeholder={`1 ~ ${maxMonths}`}
                            {...field}
                          />
                        </FormControl>
                        {subscription.platform === Platform.IOS && (
                          <FormDescription>
                            iOS는 Apple 정책상 1회 최대 3개월(90일), 1년에 2회까지
                            연장 가능합니다.
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {isProTrial && (
                  <FormField
                    control={form.control}
                    name="durationDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>연장 기간 (일) *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={365}
                            {...field}
                            onChange={(e) => {
                              const parsed = parseInt(e.target.value, 10);
                              field.onChange(
                                Number.isNaN(parsed) ? undefined : parsed
                              );
                            }}
                          />
                        </FormControl>
                        <FormDescription>1일 ~ 365일</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {isStudentPass && (
                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => {
                      const baseDate = currentEndDate ?? new Date();

                      return (
                        <FormItem className="flex flex-col">
                          <FormLabel>새 종료일 *</FormLabel>
                          <div className="flex flex-wrap gap-2">
                            {[1, 2, 3, 4].map((years) => (
                              <Button
                                key={years}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => field.onChange(addYears(baseDate, years))}
                              >
                                +{years}년
                              </Button>
                            ))}
                          </div>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  variant="outline"
                                  className={cn(
                                    "w-full pl-3 text-left font-normal",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  {field.value ? (
                                    format(field.value, "PPP", { locale: ko })
                                  ) : (
                                    <span>날짜 선택</span>
                                  )}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <Calendar
                                mode="single"
                                captionLayout="dropdown"
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(date) => date < baseDate}
                                startMonth={baseDate}
                                endMonth={addYears(new Date(), 10)}
                                locale={ko}
                              />
                            </PopoverContent>
                          </Popover>
                          <FormDescription>
                            새로운 종료일을 선택합니다.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                )}

                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>사유 (선택)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="연장 사유를 입력하세요..."
                          className="min-h-[80px]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            취소
          </Button>
          {extendCheck.allowed && (
            <Button
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isPending}
              aria-label="Confirm extension"
            >
              {isPending ? "처리 중..." : "연장 확인"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
